import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { formatDate } from "../../../../Utils/DateTimeUtils";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";

const History = () => {
  const { id } = useParams();
  const [sprint, setSprint] = useState(null);
  const [taskSnapshots, setTaskSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSprintHistory = async () => {
      try {
        setLoading(true);
        const response = await Http.get(`/sprint/${id}/history`);
        setSprint(response.data.sprint);
        setTaskSnapshots(response.data.task_snapshots);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load sprint history");
        setLoading(false);
      }
    };

    fetchSprintHistory();
  }, [id]);

  const getStatusTag = (status) => {
    let severity = "primary";
    let icon = "pi-info-circle";

    switch (status) {
      case "To Do":
        severity = "secondary";
        icon = "pi-clock";
        break;
      case "In Progress":
        severity = "info";
        icon = "pi-spin pi-spinner";
        break;
      case "Done":
      case "Completed":
        severity = "success";
        icon = "pi-check-circle";
        break;
      case "Blocked":
        severity = "danger";
        icon = "pi-exclamation-triangle";
        break;
      case "Review":
        severity = "warning";
        icon = "pi-eye";
        break;
      default:
        severity = "primary";
    }

    return (
      <Tag
        icon={`pi ${icon}`}
        value={status}
        severity={severity}
        className="px-3 py-2"
        style={{
          borderRadius: "4px",
          fontWeight: "500",
          fontSize: "11px",
        }}
      />
    );
  };

  if (loading) {
    return (
      <div className="text-center my-5 p-5">
        <div className="p-4 border-round bg-white shadow-2" style={{ maxWidth: "400px", margin: "0 auto" }}>
          <i className="pi pi-spin pi-spinner mb-3" style={{ fontSize: "2rem", color: "#3f51b5" }}></i>
          <h5 className="mb-2">Loading Sprint History</h5>
          <p className="text-muted m-0">Please wait while we retrieve the sprint details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-5 text-center p-5">
        <div className="p-4 border-round bg-white shadow-2" style={{ maxWidth: "400px", margin: "0 auto" }}>
          <i className="pi pi-exclamation-triangle mb-3" style={{ fontSize: "2.5rem", color: "#f44336" }}></i>
          <h5 className="mb-2">Something Went Wrong</h5>
          <p className="text-muted">{error}</p>
          <Button label="Try Again" icon="pi pi-refresh" className="p-button-outlined mt-3" onClick={() => window.location.reload()} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <IsLogin />
      <div className="am-mainpanel">
        <div className="am-pagetitle d-flex justify-content-between align-items-center">
          <h5 className="am-title">Sprint History</h5>
          <Link to="/sprint-list" className="btn btn-sm btn-outline-secondary">
            <i className="pi pi-arrow-left mr-2"></i>
            Back to Sprints
          </Link>
        </div>

        <div className="am-pagebody">
          {/* Sprint info card with gradient header */}
          {sprint && (
            <div className="card p-0 overflow-hidden mb-4 shadow-1">
              {/* Sprint header with gradient background */}
              <div
                className="p-3"
                style={{
                  background: "linear-gradient(135deg, #6b86c9 0%, #3a539b 100%)",
                  color: "white",
                  borderRadius: "4px 4px 0 0",
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="m-0" style={{ fontSize: "1.3rem", fontWeight: "600" }}>
                    {sprint.name}
                  </h5>
                  <Tag
                    value={sprint.status}
                    severity={sprint.status === "Completed" ? "success" : sprint.status === "Active" ? "info" : "warning"}
                    className="px-2 py-1"
                    style={{ fontSize: "0.8rem", fontWeight: "500" }}
                  />
                </div>
                <div className="d-flex align-items-center mt-1" style={{ fontSize: "0.85rem" }}>
                  <span className="mr-3">
                    <i className="pi pi-calendar mr-1"></i>
                    {formatDate(sprint.start_date)} - {formatDate(sprint.end_date)}
                  </span>
                  {sprint.goal && (
                    <span title={sprint.goal} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <i className="pi pi-flag mr-1"></i>
                      {sprint.goal.length > 40 ? sprint.goal.substring(0, 40) + "..." : sprint.goal}
                    </span>
                  )}
                </div>
              </div>

              {/* Sprint completion stats */}
              <div className="p-3 bg-white">
                <div className="row align-items-center">
                  {/* Sprint metrics */}
                  <div className="col-md-7">
                    <div className="d-flex align-items-center">
                      {/* Completed tasks counter */}
                      <div
                        className="d-flex flex-column align-items-center p-2 mr-3"
                        style={{
                          background: "#f8f9fa",
                          borderRadius: "6px",
                          minWidth: "90px",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                        }}
                      >
                        <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#4caf50", lineHeight: "1.2" }}>
                          {taskSnapshots.filter((t) => t.status_at_completion === "Done" || t.status_at_completion === "Completed").length}
                        </span>
                        <span style={{ fontSize: "0.8rem", color: "#666" }}>Completed</span>
                      </div>

                      {/* Total tasks counter */}
                      <div
                        className="d-flex flex-column align-items-center p-2 mr-3"
                        style={{
                          background: "#f8f9fa",
                          borderRadius: "6px",
                          minWidth: "90px",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                        }}
                      >
                        <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#3f51b5", lineHeight: "1.2" }}>{taskSnapshots.length}</span>
                        <span style={{ fontSize: "0.8rem", color: "#666" }}>Total</span>
                      </div>

                      {/* Status message */}
                      <div className="d-flex flex-column justify-content-center pl-1">
                        <p className="mb-0" style={{ fontSize: "0.85rem" }}>
                          <i className="pi pi-check-circle mr-1" style={{ color: "#4caf50" }}></i>
                          Sprint completed on {formatDate(sprint.completed_at)}
                        </p>
                        {sprint.project && (
                          <p className="mb-0 mt-1" style={{ fontSize: "0.85rem" }}>
                            <i className="pi pi-folder mr-1" style={{ color: "#3f51b5" }}></i>
                            {sprint.project.title || sprint.project.name || "No project assigned"}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Completion rate */}
                  <div className="col-md-5 d-flex justify-content-end align-items-center">
                    <div
                      className="text-center p-2"
                      style={{
                        background: "rgba(76, 175, 80, 0.1)",
                        borderRadius: "6px",
                        border: "1px solid rgba(76, 175, 80, 0.2)",
                      }}
                    >
                      <h3 className="mb-0" style={{ color: "#4caf50", fontSize: "1.8rem" }}>
                        {taskSnapshots.length > 0
                          ? Math.round(
                              (taskSnapshots.filter((t) => t.status_at_completion === "Done" || t.status_at_completion === "Completed").length /
                                taskSnapshots.length) *
                                100
                            )
                          : 0}
                        %
                      </h3>
                      <p className="mb-0" style={{ fontSize: "0.85rem", color: "#4caf50" }}>
                        Completion Rate
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Task history card */}
          <div className="card p-0 overflow-hidden shadow-1">
            <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center">
              <h6 className="m-0 font-weight-bold">Tasks at Sprint Completion</h6>
              <span className="badge badge-primary" style={{ backgroundColor: "#3f51b5", fontSize: "0.8rem" }}>
                {taskSnapshots.length} Tasks
              </span>
            </div>

            {taskSnapshots.length === 0 ? (
              <div className="text-center p-5">
                <i className="pi pi-inbox" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                <h5 className="mt-3 mb-1">No Tasks Found</h5>
                <p className="text-muted">No task snapshots found for this sprint.</p>
              </div>
            ) : (
              <div className="p-3">
                <div className="row mb-2 px-2">
                  <div className="col-md-5 font-weight-bold text-muted" style={{ fontSize: "0.8rem" }}>
                    TASK
                  </div>
                  <div className="col-md-2 font-weight-bold text-muted" style={{ fontSize: "0.8rem" }}>
                    STATUS
                  </div>
                  <div className="col-md-1 font-weight-bold text-muted" style={{ fontSize: "0.8rem" }}>
                    POINTS
                  </div>
                  <div className="col-md-4 font-weight-bold text-muted" style={{ fontSize: "0.8rem" }}>
                    DETAILS
                  </div>
                </div>

                {taskSnapshots.map((snapshot, index) => (
                  <div
                    key={snapshot.id}
                    className={`row p-2 mx-0 mb-2 align-items-center ${index % 2 === 0 ? "bg-white" : "bg-light"}`}
                    style={{
                      borderRadius: "6px",
                      border: "1px solid #f0f0f0",
                      transition: "all 0.2s",
                    }}
                  >
                    <div className="col-md-5">
                      <div className="d-flex align-items-center">
                        <i
                          className={`pi ${
                            snapshot.status_at_completion === "Done" || snapshot.status_at_completion === "Completed"
                              ? "pi-check-circle text-success"
                              : "pi-circle-fill text-secondary"
                          }`}
                          style={{ fontSize: "0.9rem", marginRight: "8px" }}
                        ></i>
                        <div>
                          <div className="font-weight-medium" style={{ fontSize: "0.95rem" }}>
                            {snapshot.title}
                          </div>
                          {snapshot.task && (
                            <div className="text-muted mt-1" style={{ fontSize: "0.8rem" }}>
                              {snapshot.task.taskType?.task_type || "No type"}{" "}
                              {snapshot.task.label && (
                                <span className="ml-2">
                                  <i className="pi pi-tag mr-1"></i>
                                  {snapshot.task.label.name}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">{getStatusTag(snapshot.status_at_completion)}</div>
                    <div className="col-md-1">
                      {snapshot.points ? (
                        <span
                          className="badge badge-light"
                          style={{
                            backgroundColor: "#f5f5f5",
                            color: "#666",
                            fontSize: "0.8rem",
                            padding: "4px 8px",
                            borderRadius: "4px",
                          }}
                        >
                          {snapshot.points}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </div>
                    <div className="col-md-4">
                      {snapshot.task && (
                        <Link
                          to={`/task/${snapshot.task_id}`}
                          className="btn btn-sm btn-outline-primary"
                          style={{ borderRadius: "4px", fontWeight: "500" }}
                        >
                          <i className="pi pi-external-link mr-1"></i>
                          View Task Details
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default History;
